import {memo, useMemo, useState, useEffect, useCallback} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import clsx from 'clsx';
import _cloneDeep from 'lodash/cloneDeep';
import _isEqual from 'lodash/isEqual';
import makeStyles from '@mui/styles/makeStyles';
import {agylePref, setAvatarPreference} from '../../../store/reducers/preferences';
import {getActiveAvatar, getAvatarColor, getAvatarFontColor, getUserInitials} from '../helper';
import {Grid, Typography, Box} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {user} from '../../../store/reducers/user';

const AVATAR_SIZE = {
  small: 36,
  medium: 70,
  large: 100,
}

export const useAvatarStyles = makeStyles((theme) => ({
  userAvatar: {
    display: 'flex',
    cursor: 'pointer',
    position: 'relative',
    alignItems: 'center',
    borderRadius: '50%',
    justifyContent: 'center',
    backgroundColor: '#C6DFFF',
    border: '3px solid transparent',

    '&.small': {
      borderSize: '2px',
      flexBasis: AVATAR_SIZE.small,
      width: AVATAR_SIZE.small,
      height: AVATAR_SIZE.small,
      minWidth: AVATAR_SIZE.small,
      minHeight: AVATAR_SIZE.small,
    },
    '&.medium': {
      flexBasis: AVATAR_SIZE.medium,
      width: AVATAR_SIZE.medium,
      height: AVATAR_SIZE.medium,
      minWidth: AVATAR_SIZE.medium,
      minHeight: AVATAR_SIZE.medium,
    },
    '&.large': {
      flexBasis: AVATAR_SIZE.large,
      width: AVATAR_SIZE.large,
      height: AVATAR_SIZE.large,
      minWidth: AVATAR_SIZE.large,
      minHeight: AVATAR_SIZE.large,
    },
    '&.active': {
      borderColor: theme.palette.primary.main,
    },
    '&.default': {
      fontWeight: 600,
      color: theme.palette.primary.main,
    },
    '&.editable': {
      cursor: 'unset'
    }
  },
  userInitials: {
    color: theme.palette.mode === 'light' ? '#FFF' : '#313233',

    '&.small': {
      paddingTop: 3,
    },
    '&.medium': {
    },
    '&.large': {
    },
  },
  activeBadge: {
    position: 'absolute',
    bottom: 24,
    right: 24,
    width: 0,
    height: 0,
    borderRadius: '50%',
    padding: 0,
  },
  activeIcon: {
    borderRadius: '50%',
    width: '100%',
    height: '100%',
    position: 'relative',
    backgroundColor: '#FFF',
    border: `2px solid ${theme.palette.background.default}`,

    "& path": {
      color: theme.palette.primary.main,
      transform: `scale(${22/16}) translate(-3px, -3px)`,
    },
  }
}));

const UserAvatar = ({ size, fontColor, backgroundColor, defaultAvatar, customAvatar, forceDefault, showActiveIndicator, onClick, changeAvatarModal }) => {
  const classes = useAvatarStyles();
  const dispatch = useDispatch();

  const userPreferences = useSelector(agylePref);
  const userObject = useSelector(user);

  const [isActive, setIsActive] = useState(false);

  const currentColor = useMemo(() => getAvatarColor(userPreferences), [userPreferences?.avatar]);
  // const currentFontColor = useMemo(() => getAvatarFontColor(userPreferences), [userPreferences?.avatar]);

  const isDefaultAvatar = useMemo(() => {
    return getActiveAvatar(userPreferences) === 'default';
  }, [userPreferences?.avatar]);

  useEffect(() => {
    if (currentColor === backgroundColor || !backgroundColor) {
      setIsActive(true)
    }
  }, [currentColor, isDefaultAvatar])

  const setDefaultAvatar = useCallback(() => {
    const prefsClone = _cloneDeep(userPreferences.avatar);
    prefsClone.customAvatar.active = false;
    prefsClone.defaultActive = true;

    dispatch(setAvatarPreference(prefsClone));
  }, [userPreferences?.avatar]);

  const setCustomAvatar = useCallback(() => {
    const prefsClone = _cloneDeep(userPreferences.avatar);
    prefsClone.customAvatar.active = true;
    prefsClone.defaultActive = false;

    dispatch(setAvatarPreference(prefsClone));
  }, [userPreferences?.avatar]);

  const handleAvatarClick = useCallback(() => {
    return onClick ? onClick() : () => {
      (defaultAvatar || !customAvatar) ? setDefaultAvatar() :
        setCustomAvatar();
    }
  }, [changeAvatarModal, onClick, isDefaultAvatar]);

  const initialsSize = useMemo(() => {
    switch (size) {
      case 'small':
        return 'subtitle2';
      case 'medium':
        return 'h6';
      default:
      case 'large':
        return 'h4';
    }
  }, [size])

  if (!userObject?.firstName) {
    return "";
  }

  return userObject?.firstName && (
    <Grid
      item
      sx={{p: 0}}
      onClick={handleAvatarClick}
      className={clsx(
        classes.userAvatar,
        size,
        isActive && 'active',
        getActiveAvatar(userPreferences),
      )}
      style={{ backgroundColor: backgroundColor ? backgroundColor : currentColor }}
    >
      {
        (isDefaultAvatar || forceDefault) && (
          <Typography
            variant={initialsSize}
            className={clsx(classes.userInitials, size)}
          >
            {getUserInitials(userObject)}
          </Typography>
        )
      }

      {showActiveIndicator && isActive && (
        <Box className={classes.activeBadge}>
          <CheckCircleIcon
            className={classes.activeIcon}
          />
        </Box>
      )}
    </Grid>
  )
}

UserAvatar.defaultProps = {
  size: 'small',
  fontColor: '',
  forceDefault: false,
  defaultAvatar: true,
  backgroundColor: '',
  customAvatar: false,
  changeAvatarModal: false,
  showActiveIndicator: false,
  onClick: undefined,
};

UserAvatar.propTypes = {
  fontColor: PropTypes.string,
  forceDefault: PropTypes.bool,
  defaultAvatar: PropTypes.bool,
  customAvatar: PropTypes.bool,
  backgroundColor: PropTypes.string,
  changeAvatarModal: PropTypes.bool,
  showActiveIndicator: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  onClick: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
};

export default memo(UserAvatar, _isEqual);